import React, { useState } from 'react';
import { downloadConnectedEmailAttachment } from '@core/api/integrations/integrationsApi';
import { Box, IconButton, Typography, useTheme } from '@ublend-npm/aulaui-core';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { OutlookThreadMessage } from '../types';
import { FileCopy } from '@mui/icons-material';

type AttachmentItem = NonNullable<OutlookThreadMessage['attachments']>[number];

type OutlookAttachmentProps = {
  attachment: AttachmentItem;
};

const base64ToBlob = (base64: string, contentType: string): Blob => {
  const clean = base64
    .replace(/^data:[^;]+;base64,/, '')
    .replace(/[\r\n\s]/g, '');
  const byteChars = atob(clean);
  const byteNumbers = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  return new Blob([byteNumbers], { type: contentType });
};

const getBinaryBlob = async (
  data: unknown,
  contentType?: string
): Promise<Blob> => {
  const fallbackType = contentType || 'application/octet-stream';
  if (typeof data === 'string') {
    return base64ToBlob(data, fallbackType);
  }
  if (data instanceof Blob) {
    try {
      // If API Gateway / Serverless returned base64 string text as the blob body
      const text = await data.text();
      const isBase64 =
        /^[A-Za-z0-9+/=\r\n\s]+$/.test(text) &&
        text.length > 20 &&
        !text.startsWith('%PDF') &&
        !text.startsWith('GIF8');
      if (isBase64) {
        return base64ToBlob(text, contentType || data.type || fallbackType);
      }
    } catch {
      // Ignore text read failure and use blob directly
    }
    return data;
  }
  return new Blob([data as BlobPart], { type: fallbackType });
};

/**
 * Renders a single attachment row with file icon, name/size, and download button.
 */
const OutlookAttachment = ({ attachment }: OutlookAttachmentProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const theme = useTheme();

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const { data } = await downloadConnectedEmailAttachment(
        'microsoft',
        attachment.messageId,
        attachment.id
      );
      const blob = await getBinaryBlob(data, attachment.contentType);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Attachment download failed', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      height="60px"
      p="8px"
      sx={{
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(248, 247, 249, 0.16)'
            : 'rgba(35, 31, 41, 0.16)',
        borderRadius: '4px',
        bgcolor: theme.palette.mode === 'dark' ? '#231F29' : '#F8F7F9',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={40}
        height={40}
        flexShrink={0}
        sx={{
          borderRadius: '8px',
        }}
      >
        <FileCopy sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
      </Box>

      <Box minWidth={0} flex={1}>
        <Typography
          variant="subtitle2"
          fontWeight={500}
          noWrap
          sx={{
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0.1px',
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#0F172A',
          }}
        >
          {attachment.name}
        </Typography>
        {attachment.size ? (
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            {attachment.size}
          </Typography>
        ) : null}
      </Box>

      <IconButton
        aria-label={`Download ${attachment.name}`}
        onClick={handleDownload}
        disabled={isDownloading}
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          minHeight: 40,
          p: 0,
          bgcolor: '#B0BEC5',
          color: theme.palette.mode === 'dark' ? '#231F29' : '#FFFFFF',
          '&:hover': { bgcolor: '#90A4AE' },
        }}
      >
        <FileDownloadIcon sx={{ fontSize: '24px' }} />
      </IconButton>
    </Box>
  );
};

export default OutlookAttachment;
